/**
 * MKS-241 — one wording for "why is this Robot task waiting", shared by the task
 * activity timeline and the Bot Admin held list.
 *
 * The shape mirrors the backend `RobotHeldBy` (robotHoldService.ts): an area
 * entry carries `areas` + `areaLabels`, a link entry carries `link: 'blocks'`.
 */
export interface RobotHeldBy {
  taskId?: number;
  code: string;
  areas?: string[];
  areaLabels?: string[];
  link?: 'blocks';
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

/**
 * Validates an opaque `changes.heldBy` value (state_changes payloads are not
 * schema-typed) and keeps only well-formed entries.
 */
export function parseHeldBy(value: unknown): RobotHeldBy[] {
  if (!Array.isArray(value)) return [];
  const out: RobotHeldBy[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const r = item as Record<string, unknown>;
    if (typeof r.code !== 'string' || r.code.length === 0) continue;
    const entry: RobotHeldBy = { code: r.code };
    if (typeof r.taskId === 'number') entry.taskId = r.taskId;
    if (isStringArray(r.areas)) entry.areas = r.areas;
    if (isStringArray(r.areaLabels)) entry.areaLabels = r.areaLabels;
    if (r.link === 'blocks') entry.link = 'blocks';
    out.push(entry);
  }
  return out;
}

export function heldByReason(entry: RobotHeldBy): string {
  if (entry.link === 'blocks') return 'blocks this task';
  const names = entry.areaLabels && entry.areaLabels.length > 0 ? entry.areaLabels : entry.areas;
  if (!names || names.length === 0) return 'same area';
  return `${names.length === 1 ? 'same area' : 'same areas'}: ${names.join(', ')}`;
}

/** `Waiting for MKS-12 (same area: Invoicing); MKS-15 (blocks this task)` */
export function formatHeldBy(heldBy: readonly RobotHeldBy[]): string {
  if (heldBy.length === 0) return 'Waiting';
  return `Waiting for ${heldBy.map((h) => `${h.code} (${heldByReason(h)})`).join('; ')}`;
}
